import { useState, useMemo, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useSiadap, type RegistoSiadap, type AvaliacaoSiadap } from '@/context/SiadapContext';
import { useAppSettings } from '@/context/AppSettingsContext';
import { MUNICIPIO_SIADAP_ID } from '@/lib/municipios';
import { calcularPontosCiclo, type PontosEscalasConfig } from '@/utils/progressaoUtils';

// ─── Tipos e constantes ───────────────────────────────────────────────────────

const AVALIACOES_PRE2023: AvaliacaoSiadap[] = ['Excelente', 'Relevante', 'Adequado', 'Inadequado'];
const AVALIACOES_MODERNAS: AvaliacaoSiadap[] = ['Excelente', 'Muito Bom', 'Bom', 'Regular', 'Inadequado'];

const CORES_AVALIACAO: Record<string, { bg: string; text: string; dot: string; border: string }> = {
  Excelente:   { bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500', border: 'border-emerald-300' },
  'Muito Bom': { bg: 'bg-amber-50',   text: 'text-amber-700',   dot: 'bg-amber-500',   border: 'border-amber-300'   },
  Bom:         { bg: 'bg-zinc-100',   text: 'text-zinc-600',    dot: 'bg-zinc-400',    border: 'border-zinc-300'    },
  Regular:     { bg: 'bg-orange-50',  text: 'text-orange-700',  dot: 'bg-orange-400',  border: 'border-orange-300'  },
  Inadequado:  { bg: 'bg-rose-50',    text: 'text-rose-700',    dot: 'bg-rose-500',    border: 'border-rose-300'    },
  Relevante:   { bg: 'bg-teal-50',    text: 'text-teal-700',    dot: 'bg-teal-500',    border: 'border-teal-300'    },
  Adequado:    { bg: 'bg-zinc-100',   text: 'text-zinc-600',    dot: 'bg-zinc-400',    border: 'border-zinc-300'    },
};

type TipoEscala = 'pre2023' | '2023_2024' | '2025plus';

function tipoEscalaDoPeriodo(periodo: string): TipoEscala {
  const partes = periodo.trim().split('-');
  const ultimo = partes[partes.length - 1];
  const ano = parseInt(ultimo, 10);
  if (isNaN(ano)) return '2025plus';
  if (ano < 2023) return 'pre2023';
  if (ano < 2025) return '2023_2024';
  return '2025plus';
}

const ESCALA_LABELS: Record<TipoEscala, { label: string; bg: string; text: string }> = {
  pre2023:     { label: 'Escala anterior a 2023', bg: 'bg-orange-50',  text: 'text-orange-700'  },
  '2023_2024': { label: 'Escala 2023–2024',       bg: 'bg-zinc-100',   text: 'text-zinc-600'    },
  '2025plus':  { label: 'Escala atual (2025+)',    bg: 'bg-emerald-50', text: 'text-emerald-700' },
};

// ─── Formulário inline ────────────────────────────────────────────────────────

interface FormState {
  periodoAvaliacao: string;
  avaliacao: AvaliacaoSiadap;
  pontosChefe: number;
  pontosCCA: number;
  nivel: number;
  remuneracaoAtual: number;
  descricaoSubida: string;
}

const FORM_VAZIO: FormState = {
  periodoAvaliacao: '2023-2024',
  avaliacao: 'Bom',
  pontosChefe: 3.0,
  pontosCCA: 3.0,
  nivel: 1,
  remuneracaoAtual: 800,
  descricaoSubida: '',
};

interface InlineFormProps {
  funcionarioId: string;
  editando?: RegistoSiadap | null;
  onSaved: () => void;
  onCancel: () => void;
  pontosMap: Record<string, number>;
  registosSiadap: RegistoSiadap[];
}

function InlineForm({ funcionarioId, editando, onSaved, onCancel, pontosMap: pontosMapProp, registosSiadap }: InlineFormProps) {
  const { settings } = useAppSettings();

  const [form, setForm] = useState<FormState>(() =>
    editando
      ? {
          periodoAvaliacao: editando.periodoAvaliacao,
          avaliacao: editando.avaliacao,
          pontosChefe: editando.pontosChefe,
          pontosCCA: editando.pontosCCA,
          nivel: editando.nivel,
          remuneracaoAtual: editando.remuneracaoAtual,
          descricaoSubida: editando.descricaoSubida,
        }
      : FORM_VAZIO
  );

  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState | 'avaliacao', string>>>({});

  const tipoEscala = tipoEscalaDoPeriodo(form.periodoAvaliacao);
  const avaliacoesDisponiveis = tipoEscala === 'pre2023' ? AVALIACOES_PRE2023 : AVALIACOES_MODERNAS;
  const escalaInfo = ESCALA_LABELS[tipoEscala];

  const pontosMapLocal = useMemo((): Record<string, number> => {
    if (tipoEscala === 'pre2023') {
      return {
        Excelente:  settings.pontos_excelente_pre2023,
        Relevante:  settings.pontos_relevante_pre2023,
        Adequado:   settings.pontos_adequado_pre2023,
        Inadequado: settings.pontos_inadequado_pre2023,
      };
    }
    if (tipoEscala === '2023_2024') {
      return {
        Excelente:   settings.pontos_excelente_2023,
        'Muito Bom': settings.pontos_muito_bom_2023,
        Bom:         settings.pontos_bom_2023,
        Regular:     settings.pontos_regular_2023,
        Inadequado:  settings.pontos_inadequado_2023,
      };
    }
    return {
      Excelente:   settings.pontos_excelente,
      'Muito Bom': settings.pontos_muito_bom,
      Bom:         settings.pontos_bom,
      Regular:     settings.pontos_regular,
      Inadequado:  settings.pontos_inadequado,
    };
  }, [tipoEscala, settings]);

  function set<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  }

  function handlePeriodoChange(novoPeriodo: string) {
    const novoTipo = tipoEscalaDoPeriodo(novoPeriodo);
    const novasAvaliacoes = novoTipo === 'pre2023' ? AVALIACOES_PRE2023 : AVALIACOES_MODERNAS;
    setForm((prev) => ({
      ...prev,
      periodoAvaliacao: novoPeriodo,
      avaliacao: novasAvaliacoes.includes(prev.avaliacao) ? prev.avaliacao : novasAvaliacoes[0],
    }));
    setErrors((prev) => ({ ...prev, periodoAvaliacao: undefined }));
  }

  function validate(): boolean {
    const errs: Partial<Record<keyof FormState | 'avaliacao', string>> = {};
    if (!form.periodoAvaliacao.trim()) {
      errs.periodoAvaliacao = 'Período obrigatório';
    } else {
      const duplicado = registosSiadap.some(
        (r) =>
          r.funcionarioId === funcionarioId &&
          r.periodoAvaliacao.trim().toLowerCase() === form.periodoAvaliacao.trim().toLowerCase() &&
          r.id !== editando?.id
      );
      if (duplicado) errs.periodoAvaliacao = `Já existe avaliação para "${form.periodoAvaliacao}"`;
    }
    if (form.pontosChefe < 1 || form.pontosChefe > 5) errs.pontosChefe = 'Entre 1.0 e 5.0';
    if (form.pontosCCA < 1 || form.pontosCCA > 5) errs.pontosCCA = 'Entre 1.0 e 5.0';
    if (form.nivel < 1) errs.nivel = 'Mínimo 1';
    if (form.remuneracaoAtual < 0) errs.remuneracaoAtual = 'Valor inválido';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  async function handleSave() {
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = {
        funcionario_id: funcionarioId,
        periodo_avaliacao: form.periodoAvaliacao.trim(),
        avaliacao: form.avaliacao,
        pontos_chefe: form.pontosChefe,
        pontos_cca: form.pontosCCA,
        nivel: form.nivel,
        remuneracao_atual: form.remuneracaoAtual,
        descricao_subida: form.descricaoSubida,
        municipio_id: MUNICIPIO_SIADAP_ID,
      };

      if (editando) {
        const { error } = await supabase
          .from('registos_siadap')
          .update({ ...payload, updated_at: new Date().toISOString() })
          .eq('id', editando.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('registos_siadap')
          .insert({ ...payload, id: 's' + Date.now().toString(36) });
        if (error) throw error;
      }
      onSaved();
    } catch (e: unknown) {
      setErrors({ periodoAvaliacao: e instanceof Error ? e.message : 'Erro ao guardar' });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="bg-white rounded-2xl border border-zinc-200 overflow-hidden">
      {/* Header do formulário */}
      <div className="px-5 py-4 border-b border-zinc-100 flex items-center justify-between bg-zinc-50/60">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-zinc-900">
            <i className={`${editando ? 'ri-edit-line' : 'ri-add-line'} text-white text-sm`} />
          </div>
          <div>
            <p className="text-[13px] font-bold text-zinc-800">
              {editando ? 'Editar Avaliação SIADAP' : 'Nova Avaliação SIADAP'}
            </p>
            <p className="text-[11px] text-zinc-400">Preencha os dados da avaliação</p>
          </div>
        </div>
        <button
          onClick={onCancel}
          className="whitespace-nowrap w-7 h-7 flex items-center justify-center rounded-lg text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 transition-colors cursor-pointer"
        >
          <i className="ri-close-line text-base" />
        </button>
      </div>

      <div className="px-5 py-5 space-y-5">
        {/* Período + Escala */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-[11px] font-semibold text-zinc-500 uppercase tracking-widest">
              Período de Avaliação *
            </label>
            <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${escalaInfo.bg} ${escalaInfo.text}`}>
              {escalaInfo.label}
            </span>
          </div>
          <input
            type="text"
            value={form.periodoAvaliacao}
            onChange={(e) => handlePeriodoChange(e.target.value)}
            placeholder="Ex: 2023-2024 ou 2022"
            className={`w-full px-3 py-2.5 text-[13px] border rounded-lg bg-white text-zinc-700 focus:outline-none focus:ring-1 focus:ring-zinc-300 ${errors.periodoAvaliacao ? 'border-rose-300 bg-rose-50/20' : 'border-zinc-200'}`}
          />
          {errors.periodoAvaliacao && (
            <p className="text-[11px] text-rose-500 mt-1 flex items-center gap-1">
              <i className="ri-error-warning-line text-xs" />{errors.periodoAvaliacao}
            </p>
          )}
          <p className="text-[11px] text-zinc-400 mt-1">
            Formato: ano-ano (ex: 2023-2024). A escala muda automaticamente.
          </p>
        </div>

        {/* Avaliação */}
        <div>
          <label className="block text-[11px] font-semibold text-zinc-500 uppercase tracking-widest mb-2">
            Avaliação *
          </label>
          <div className="flex flex-wrap gap-2">
            {avaliacoesDisponiveis.map((a) => {
              const pts = pontosMapLocal[a] ?? 0;
              const isSelected = form.avaliacao === a;
              const cfg = CORES_AVALIACAO[a] ?? CORES_AVALIACAO['Bom'];
              return (
                <button
                  key={a}
                  type="button"
                  onClick={() => set('avaliacao', a)}
                  className={`whitespace-nowrap flex items-center gap-2 px-3 py-2 text-[12px] font-medium rounded-xl border-2 transition-all cursor-pointer ${
                    isSelected
                      ? `${cfg.bg} ${cfg.text} ${cfg.border}`
                      : 'bg-white text-zinc-500 border-zinc-200 hover:border-zinc-300'
                  }`}
                >
                  <span className={`w-2 h-2 rounded-full flex-shrink-0 ${isSelected ? cfg.dot : 'bg-zinc-300'}`} />
                  {a}
                  <span className={`text-[11px] font-bold px-1.5 py-0.5 rounded-lg ${isSelected ? 'bg-white/60' : 'bg-zinc-100 text-zinc-400'}`}>
                    {pts % 1 === 0 ? pts.toFixed(0) : pts.toFixed(1)} pts
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Pontuações */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-[11px] font-semibold text-zinc-500 uppercase tracking-widest mb-1.5">
              Pontos Chefia (1–5) *
            </label>
            <input
              type="number" min={1} max={5} step={0.1}
              value={form.pontosChefe}
              onChange={(e) => set('pontosChefe', parseFloat(e.target.value) || 1)}
              className={`w-full px-3 py-2.5 text-[13px] border rounded-lg bg-white text-zinc-700 focus:outline-none focus:ring-1 focus:ring-zinc-300 ${errors.pontosChefe ? 'border-rose-300' : 'border-zinc-200'}`}
            />
            {errors.pontosChefe && <p className="text-[11px] text-rose-500 mt-1">{errors.pontosChefe}</p>}
            <div className="mt-1.5 h-1 bg-zinc-100 rounded-full overflow-hidden">
              <div className="h-full bg-zinc-500 rounded-full transition-all" style={{ width: `${((form.pontosChefe - 1) / 4) * 100}%` }} />
            </div>
          </div>
          <div>
            <label className="block text-[11px] font-semibold text-zinc-500 uppercase tracking-widest mb-1.5">
              Pontos CCA (1–5) *
            </label>
            <input
              type="number" min={1} max={5} step={0.1}
              value={form.pontosCCA}
              onChange={(e) => set('pontosCCA', parseFloat(e.target.value) || 1)}
              className={`w-full px-3 py-2.5 text-[13px] border rounded-lg bg-white text-zinc-700 focus:outline-none focus:ring-1 focus:ring-zinc-300 ${errors.pontosCCA ? 'border-rose-300' : 'border-zinc-200'}`}
            />
            {errors.pontosCCA && <p className="text-[11px] text-rose-500 mt-1">{errors.pontosCCA}</p>}
            <div className="mt-1.5 h-1 bg-zinc-100 rounded-full overflow-hidden">
              <div className="h-full bg-emerald-500 rounded-full transition-all" style={{ width: `${((form.pontosCCA - 1) / 4) * 100}%` }} />
            </div>
          </div>
        </div>

        {/* Nível + Remuneração */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-[11px] font-semibold text-zinc-500 uppercase tracking-widest mb-1.5">
              Nível *
            </label>
            <input
              type="number" min={1} max={50}
              value={form.nivel}
              onChange={(e) => set('nivel', parseInt(e.target.value, 10) || 1)}
              className={`w-full px-3 py-2.5 text-[13px] border rounded-lg bg-white text-zinc-700 focus:outline-none focus:ring-1 focus:ring-zinc-300 ${errors.nivel ? 'border-rose-300' : 'border-zinc-200'}`}
            />
            {errors.nivel && <p className="text-[11px] text-rose-500 mt-1">{errors.nivel}</p>}
          </div>
          <div>
            <label className="block text-[11px] font-semibold text-zinc-500 uppercase tracking-widest mb-1.5">
              Remuneração (€) *
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[13px] text-zinc-400 font-medium">€</span>
              <input
                type="number" min={0} step={0.01}
                value={form.remuneracaoAtual}
                onChange={(e) => set('remuneracaoAtual', parseFloat(e.target.value) || 0)}
                className={`w-full pl-7 pr-3 py-2.5 text-[13px] border rounded-lg bg-white text-zinc-700 focus:outline-none focus:ring-1 focus:ring-zinc-300 ${errors.remuneracaoAtual ? 'border-rose-300' : 'border-zinc-200'}`}
              />
            </div>
            {errors.remuneracaoAtual && <p className="text-[11px] text-rose-500 mt-1">{errors.remuneracaoAtual}</p>}
          </div>
        </div>

        {/* Observações */}
        <div>
          <label className="block text-[11px] font-semibold text-zinc-500 uppercase tracking-widest mb-1.5">
            Observações
          </label>
          <textarea
            value={form.descricaoSubida}
            onChange={(e) => set('descricaoSubida', e.target.value)}
            rows={2}
            maxLength={500}
            placeholder="Notas relevantes sobre esta avaliação..."
            className="w-full px-3 py-2.5 text-[13px] border border-zinc-200 rounded-lg bg-white text-zinc-700 placeholder-zinc-400 focus:outline-none focus:ring-1 focus:ring-zinc-300 resize-none"
          />
          <div className="flex justify-end mt-0.5">
            <span className={`text-[11px] ${form.descricaoSubida.length > 450 ? 'text-amber-500' : 'text-zinc-400'}`}>
              {form.descricaoSubida.length}/500
            </span>
          </div>
        </div>

        {/* Botões */}
        <div className="flex items-center justify-end gap-2 pt-1 border-t border-zinc-100">
          <button
            type="button"
            onClick={onCancel}
            className="whitespace-nowrap px-4 py-2 text-[13px] font-medium text-zinc-600 border border-zinc-200 rounded-lg hover:bg-zinc-50 transition-colors cursor-pointer"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="whitespace-nowrap flex items-center gap-2 px-5 py-2 text-[13px] font-semibold text-white bg-zinc-900 rounded-lg hover:bg-zinc-800 transition-colors cursor-pointer disabled:opacity-60"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className={`${saving ? 'ri-loader-4-line animate-spin' : editando ? 'ri-save-line' : 'ri-add-line'} text-sm`} />
            </div>
            {saving ? 'A guardar...' : editando ? 'Guardar Alterações' : 'Adicionar Registo'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Componente principal ─────────────────────────────────────────────────────

interface Props {
  funcionarioId: string;
  pontosDescontados: number;
}

export default function SiadapTab({ funcionarioId, pontosDescontados }: Props) {
  const { registosSiadap, registosHistorico, reloadSiadap } = useSiadap();
  const { settings } = useAppSettings();

  const [showForm, setShowForm] = useState(false);
  const [editando, setEditando] = useState<RegistoSiadap | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState('');

  const siadapDoFunc = useMemo(
    () => registosSiadap.filter((r) => r.funcionarioId === funcionarioId),
    [registosSiadap, funcionarioId]
  );
  const historicoDoFunc = useMemo(
    () => registosHistorico.filter((r) => r.funcionarioId === funcionarioId),
    [registosHistorico, funcionarioId]
  );

  const escalas: PontosEscalasConfig = {
    pos2025: {
      Excelente: settings.pontos_excelente,
      'Muito Bom': settings.pontos_muito_bom,
      Bom: settings.pontos_bom,
      Regular: settings.pontos_regular,
      Inadequado: settings.pontos_inadequado,
    },
    p2023_2024: {
      Excelente: settings.pontos_excelente_2023,
      'Muito Bom': settings.pontos_muito_bom_2023,
      Bom: settings.pontos_bom_2023,
      Regular: settings.pontos_regular_2023,
      Inadequado: settings.pontos_inadequado_2023,
    },
    pre2023: {
      Excelente: settings.pontos_excelente_pre2023,
      Relevante: settings.pontos_relevante_pre2023,
      Adequado: settings.pontos_adequado_pre2023,
      Inadequado: settings.pontos_inadequado_pre2023,
      'Muito Bom': settings.pontos_relevante_pre2023,
      Bom: settings.pontos_adequado_pre2023,
      Regular: settings.pontos_adequado_pre2023,
    },
  };

  const pontosMap: Partial<Record<AvaliacaoSiadap, number>> = {
    Excelente: settings.pontos_excelente,
    'Muito Bom': settings.pontos_muito_bom,
    Bom: settings.pontos_bom,
    Regular: settings.pontos_regular,
    Inadequado: settings.pontos_inadequado,
  };

  const totalPontos = calcularPontosCiclo(
    siadapDoFunc,
    historicoDoFunc,
    pontosDescontados,
    0,
    pontosMap,
    undefined,
    escalas
  );

  function showSuccess(msg: string) {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(''), 3000);
  }

  const handleSaved = useCallback(async () => {
    await reloadSiadap();
    setShowForm(false);
    setEditando(null);
    showSuccess(editando ? 'Avaliação atualizada com sucesso!' : 'Avaliação adicionada com sucesso!');
  }, [reloadSiadap, editando]);

  function openEdit(r: RegistoSiadap) {
    setEditando(r);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function openNew() {
    setEditando(null);
    setShowForm(true);
  }

  function cancelForm() {
    setShowForm(false);
    setEditando(null);
  }

  async function handleDelete(id: string) {
    setDeletingId(id);
    try {
      const { error } = await supabase
        .from('registos_siadap')
        .delete()
        .eq('id', id);
      if (error) throw error;
      await reloadSiadap();
      setConfirmDeleteId(null);
      showSuccess('Avaliação eliminada.');
    } catch (e: unknown) {
      showSuccess('Erro ao eliminar: ' + (e instanceof Error ? e.message : 'Erro desconhecido'));
    } finally {
      setDeletingId(null);
    }
  }

  // Ordenar por período (mais recente primeiro)
  const siadapOrdenado = useMemo(
    () => [...siadapDoFunc].sort((a, b) => b.periodoAvaliacao.localeCompare(a.periodoAvaliacao)),
    [siadapDoFunc]
  );

  return (
    <div className="space-y-5">
      {/* Toast */}
      {successMsg && (
        <div className="flex items-center gap-3 bg-emerald-50 border border-emerald-200 text-emerald-700 text-[13px] px-4 py-3 rounded-xl">
          <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
            <i className="ri-checkbox-circle-line text-base" />
          </div>
          {successMsg}
        </div>
      )}

      {/* Formulário inline */}
      {showForm && (
        <InlineForm
          funcionarioId={funcionarioId}
          editando={editando}
          onSaved={handleSaved}
          onCancel={cancelForm}
          pontosMap={pontosMap as Record<string, number>}
          registosSiadap={registosSiadap}
        />
      )}

      {/* Header + botão novo */}
      {!showForm && (
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-[15px] font-bold text-zinc-800">Avaliações SIADAP</h2>
            <p className="text-[12px] text-zinc-400 mt-0.5">
              {siadapDoFunc.length} registo(s) · {totalPontos.toFixed(1)} pts acumulados
            </p>
          </div>
          <button
            onClick={openNew}
            className="whitespace-nowrap flex items-center gap-2 bg-zinc-900 text-white text-[13px] font-medium px-4 py-2.5 rounded-lg hover:bg-zinc-800 transition-colors cursor-pointer"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-add-line text-sm" />
            </div>
            Nova Avaliação
          </button>
        </div>
      )}

      {/* Lista de avaliações SIADAP */}
      {siadapOrdenado.length > 0 ? (
        <div className="bg-white rounded-2xl border border-zinc-100 overflow-hidden">
          <div className="px-5 py-4 border-b border-zinc-50 flex items-center gap-2">
            <div className="w-7 h-7 flex items-center justify-center bg-zinc-100 rounded-lg">
              <i className="ri-award-line text-zinc-500 text-sm" />
            </div>
            <h3 className="text-[13px] font-semibold text-zinc-700">
              Registos SIADAP ({siadapOrdenado.length})
            </h3>
          </div>
          <div className="divide-y divide-zinc-50">
            {siadapOrdenado.map((r) => {
              const cfg = CORES_AVALIACAO[r.avaliacao] ?? CORES_AVALIACAO['Bom'];
              const tipoEscala = tipoEscalaDoPeriodo(r.periodoAvaliacao);
              const escalaInfo = ESCALA_LABELS[tipoEscala];
              const isConfirmDelete = confirmDeleteId === r.id;
              const isDeleting = deletingId === r.id;

              return (
                <div
                  key={r.id}
                  className="px-5 py-4 hover:bg-zinc-50/60 transition-colors group"
                >
                  <div className="flex items-start justify-between gap-4">
                    {/* Info principal */}
                    <div className="flex items-center gap-4 flex-1 min-w-0">
                      {/* Período */}
                      <div className="flex-shrink-0">
                        <p className="text-[15px] font-bold text-zinc-800">{r.periodoAvaliacao}</p>
                        <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${escalaInfo.bg} ${escalaInfo.text}`}>
                          {escalaInfo.label}
                        </span>
                      </div>

                      {/* Avaliação badge */}
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[12px] font-semibold flex-shrink-0 ${cfg.bg} ${cfg.text}`}>
                        <span className={`w-2 h-2 rounded-full ${cfg.dot}`} />
                        {r.avaliacao}
                      </span>

                      {/* Pontuações */}
                      <div className="hidden sm:flex items-center gap-4 text-[12px] text-zinc-500 flex-shrink-0">
                        <div className="flex items-center gap-1">
                          <i className="ri-user-star-line text-zinc-400 text-xs" />
                          <span>Chefia: <strong className="text-zinc-700">{r.pontosChefe.toFixed(1)}</strong></span>
                        </div>
                        <div className="flex items-center gap-1">
                          <i className="ri-group-line text-zinc-400 text-xs" />
                          <span>CCA: <strong className="text-zinc-700">{r.pontosCCA.toFixed(1)}</strong></span>
                        </div>
                        <div className="flex items-center gap-1">
                          <i className="ri-bar-chart-line text-zinc-400 text-xs" />
                          <span>Nível: <strong className="text-zinc-700">{r.nivel}</strong></span>
                        </div>
                      </div>
                    </div>

                    {/* Ações */}
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {isConfirmDelete ? (
                        <div className="flex items-center gap-1.5">
                          <span className="text-[11px] text-zinc-500">Eliminar?</span>
                          <button
                            onClick={() => handleDelete(r.id)}
                            disabled={isDeleting}
                            className="whitespace-nowrap text-[11px] font-semibold text-white bg-rose-500 hover:bg-rose-600 px-2.5 py-1 rounded-lg cursor-pointer disabled:opacity-50 transition-colors"
                          >
                            {isDeleting ? <i className="ri-loader-4-line animate-spin text-xs" /> : 'Sim'}
                          </button>
                          <button
                            onClick={() => setConfirmDeleteId(null)}
                            className="whitespace-nowrap text-[11px] text-zinc-500 bg-zinc-100 hover:bg-zinc-200 px-2.5 py-1 rounded-lg cursor-pointer transition-colors"
                          >
                            Não
                          </button>
                        </div>
                      ) : (
                        <>
                          <button
                            onClick={() => openEdit(r)}
                            className="whitespace-nowrap w-8 h-8 flex items-center justify-center rounded-lg text-zinc-300 hover:text-zinc-700 hover:bg-zinc-100 transition-colors cursor-pointer opacity-0 group-hover:opacity-100"
                            title="Editar"
                          >
                            <i className="ri-edit-line text-sm" />
                          </button>
                          <button
                            onClick={() => setConfirmDeleteId(r.id)}
                            className="whitespace-nowrap w-8 h-8 flex items-center justify-center rounded-lg text-zinc-300 hover:text-rose-500 hover:bg-rose-50 transition-colors cursor-pointer opacity-0 group-hover:opacity-100"
                            title="Eliminar"
                          >
                            <i className="ri-delete-bin-line text-sm" />
                          </button>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Observações */}
                  {r.descricaoSubida && (
                    <p className="mt-2 text-[12px] text-zinc-400 leading-relaxed pl-0 border-t border-zinc-50 pt-2">
                      {r.descricaoSubida}
                    </p>
                  )}

                  {/* Remuneração */}
                  <div className="mt-2 flex items-center gap-1 text-[11px] text-zinc-400">
                    <i className="ri-money-euro-circle-line text-xs" />
                    <span>Remuneração: <strong className="text-zinc-600">{r.remuneracaoAtual.toLocaleString('pt-PT', { style: 'currency', currency: 'EUR' })}</strong></span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : !showForm ? (
        <div className="bg-white rounded-2xl border border-zinc-100 py-16 flex flex-col items-center gap-3 text-zinc-400">
          <div className="w-14 h-14 flex items-center justify-center bg-zinc-50 rounded-2xl">
            <i className="ri-award-line text-3xl text-zinc-300" />
          </div>
          <p className="text-[14px] font-semibold text-zinc-400">Sem avaliações SIADAP</p>
          <p className="text-[12px] text-zinc-300">Adiciona a primeira avaliação para este funcionário.</p>
          <button
            onClick={openNew}
            className="whitespace-nowrap flex items-center gap-2 mt-2 bg-zinc-900 text-white text-[13px] font-medium px-4 py-2.5 rounded-lg hover:bg-zinc-800 transition-colors cursor-pointer"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-add-line text-sm" />
            </div>
            Adicionar Avaliação
          </button>
        </div>
      ) : null}

      {/* Histórico de progressões (só leitura) */}
      {historicoDoFunc.length > 0 && (
        <div className="bg-white rounded-2xl border border-zinc-100 overflow-hidden">
          <div className="px-5 py-4 border-b border-zinc-50 flex items-center gap-2">
            <div className="w-7 h-7 flex items-center justify-center bg-zinc-100 rounded-lg">
              <i className="ri-history-line text-zinc-500 text-sm" />
            </div>
            <h3 className="text-[13px] font-semibold text-zinc-700">
              Histórico de Avaliações ({historicoDoFunc.length})
            </h3>
            <span className="text-[11px] text-zinc-400 ml-auto">Importado do histórico</span>
          </div>
          <div className="divide-y divide-zinc-50">
            {[...historicoDoFunc]
              .sort((a, b) => b.ano - a.ano)
              .map((r) => {
                const cfg = CORES_AVALIACAO[r.classificacaoServico] ?? CORES_AVALIACAO['Bom'];
                return (
                  <div key={r.id} className="flex items-center justify-between px-5 py-3 hover:bg-zinc-50/60 transition-colors">
                    <div className="flex items-center gap-3">
                      <span className="text-[14px] font-bold text-zinc-700 w-12">{r.ano}</span>
                      <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-semibold ${cfg.bg} ${cfg.text}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
                        {r.classificacaoServico}
                      </span>
                      <div className="hidden sm:flex items-center gap-3 text-[11px] text-zinc-400">
                        <span>Chefia: <strong className="text-zinc-600">{r.pontuacaoChefe.toFixed(1)}</strong></span>
                        <span>CCA: <strong className="text-zinc-600">{r.pontuacaoCCA.toFixed(1)}</strong></span>
                      </div>
                    </div>
                    <span className="text-[11px] text-zinc-300 italic">histórico</span>
                  </div>
                );
              })}
          </div>
        </div>
      )}
    </div>
  );
}
